/** Wire-контракт ставки НДС товаров 1С (`VatDtos.cs`) и проверка на границе. */
import { bool, fail, isRecord, list, num, optionalText, text } from '../../../shared/api/onecEnvelope'
import type {
  VatApplyResult,
  VatCount,
  VatGroup,
  VatItemOutcome,
  VatItemResult,
  VatPreview,
  VatRateOption,
  VatSkip,
  VatSummary,
} from '../model/types'

export const ONEC_VAT_SCHEMAS = {
  preview: 'onec.business.Catalog_VatPreview.v1',
  apply: 'onec.business.Catalog_ApplyVat.v1',
} as const

function record(raw: unknown, what: string): Record<string, unknown> {
  return isRecord(raw) ? raw : fail(`${what} — не объект`)
}

function optionalNum(value: unknown, field: string): number | null {
  return value === undefined || value === null ? null : num(value, field)
}

const decodeRate = (raw: unknown, index: number): VatRateOption => {
  const item = record(raw, `ставка[${index}]`)
  return { code: text(item.code, 'code'), name: text(item.name, 'name') }
}

const decodeCount = (raw: unknown, index: number): VatCount => {
  const item = record(raw, `счёт ставки[${index}]`)
  return { code: optionalText(item.code, 'code'), name: text(item.name, 'name'), count: num(item.count, 'count') }
}

const decodeGroup = (raw: unknown, index: number): VatGroup => {
  const item = record(raw, `группа[${index}]`)
  return {
    key: text(item.key, 'key'),
    parentKey: optionalText(item.parentKey, 'parentKey'),
    name: text(item.name, 'name'),
    total: num(item.total, 'total'),
    withoutVat: num(item.withoutVat, 'withoutVat'),
  }
}

export function decodeVatSummary(raw: unknown): VatSummary {
  const item = record(raw, 'сводка НДС')
  const available = bool(item.available, 'available')
  const total = optionalNum(item.total, 'total')
  const withVat = optionalNum(item.withVat, 'withVat')
  const withoutVat = optionalNum(item.withoutVat, 'withoutVat')
  // Данные есть — числа обязаны быть: иначе экран показал бы нули вместо «нет данных».
  if (available && (total === null || withVat === null || withoutVat === null)) fail('сводка НДС: данные есть, а чисел нет')
  return {
    available,
    reason: optionalText(item.reason, 'reason'),
    total,
    withVat,
    withoutVat,
    markedForDeletion: num(item.markedForDeletion, 'markedForDeletion'),
    byRate: list(item.byRate, 'byRate', decodeCount),
    rates: list(item.rates, 'rates', decodeRate),
    groups: list(item.groups, 'groups', decodeGroup),
  }
}

export function decodeVatPreview(raw: unknown): VatPreview {
  const item = record(raw, 'предпросмотр НДС')
  return {
    rateCode: text(item.rateCode, 'rateCode'),
    rateName: text(item.rateName, 'rateName'),
    groupKey: optionalText(item.groupKey, 'groupKey'),
    groupName: optionalText(item.groupName, 'groupName'),
    inScope: num(item.inScope, 'inScope'),
    willChange: num(item.willChange, 'willChange'),
    alreadySet: num(item.alreadySet, 'alreadySet'),
    skipped: list(item.skipped, 'skipped', (entry, index): VatSkip => {
      const skip = record(entry, `пропуск[${index}]`)
      return { reason: text(skip.reason, 'reason'), text: text(skip.text, 'text'), count: num(skip.count, 'count') }
    }),
    refKeys: list(item.refKeys, 'refKeys', (key, index) => text(key, `refKeys[${index}]`)),
  }
}

const OUTCOMES: readonly VatItemOutcome[] = ['Changed', 'AlreadySet', 'NotFound', 'Failed', 'Unknown', 'NotProcessed']

const decodeItem = (raw: unknown, index: number): VatItemResult => {
  const item = record(raw, `товар[${index}]`)
  const outcome = text(item.outcome, 'outcome')
  if (!OUTCOMES.includes(outcome as VatItemOutcome)) fail(`товар[${index}]: неизвестный исход «${outcome}»`)
  return {
    refKey: text(item.refKey, 'refKey'),
    outcome: outcome as VatItemOutcome,
    message: optionalText(item.message, 'message'),
    name: optionalText(item.name, 'name'),
    article: optionalText(item.article, 'article'),
  }
}

export function decodeVatApply(raw: unknown): VatApplyResult {
  const item = record(raw, 'итог записи НДС')
  return {
    changed: num(item.changed, 'changed'),
    alreadySet: num(item.alreadySet, 'alreadySet'),
    failed: num(item.failed, 'failed'),
    unknown: num(item.unknown, 'unknown'),
    notProcessed: num(item.notProcessed, 'notProcessed'),
    items: list(item.items, 'items', decodeItem),
  }
}
